import Configuration from "./configuration";
import { LocalUserState, MediaDevicesState, RoomState } from "./state";

export type StoredSettings = Partial<
    Pick<MediaDevicesState, "audioInputDeviceId" | "videoInputDeviceId" | "audioOutputDeviceId"> &
        Pick<LocalUserState, "displayName"> &
        Pick<RoomState, "videoLayout">
>;

/** Shorthand wrapper fn with strong typing */
export function getItem(name: keyof StoredSettings): string | null {
    return window.localStorage.getItem(`${Configuration.LS_NAMESPACE}_${name}`);
}

/** Shorthand wrapper fn with loose typing */
export function getDynamicItem(name: string): string | null {
    return getItem(name as any);
}

/**
 * If LocalStorage is available, check if we saved any settings there,
 * otherwise return the defaults
 */
export function getDefaultUserSettings(roomName?: string): StoredSettings {
    if (typeof window !== "undefined" && window.localStorage != null) {
        const settings: StoredSettings = {
            audioInputDeviceId: getItem("audioInputDeviceId") ?? "default",
            videoInputDeviceId: getItem("videoInputDeviceId") ?? "default",
            audioOutputDeviceId: getItem("audioOutputDeviceId") ?? "default",
            displayName: getItem("displayName") ?? undefined,
        };

        // items with dynamic keys
        if (roomName !== undefined) {
            const key = getRoomVideoLayoutLSKey(roomName);
            const layout = getDynamicItem(key);
            if (layout != null) {
                try {
                    settings.videoLayout = JSON.parse(layout);
                } catch (e) {
                    console.error("Unable to load stored video layout", e);
                    persistSetting(key as any, undefined);
                }
            }
        }

        return settings;
    }

    return {
        audioInputDeviceId: "default",
        videoInputDeviceId: "default",
        audioOutputDeviceId: "default",
    };
}

export function persistSetting(name: keyof StoredSettings, value: any) {
    if (typeof window !== "undefined" && window.localStorage != null) {
        if (typeof value !== "string") {
            value = JSON.stringify(value);
        }
        window.localStorage.setItem(`${Configuration.LS_NAMESPACE}_${name}`, value);
    }
}

/**
 * Room video layouts are on a per-room basis
 */
export function getRoomVideoLayoutLSKey(roomName: string) {
    // don't include namespace, because persistSeting() will prefix it
    return `${roomName}_videoLayout`;
}
